/**
 * Recommendations Alert Banner
 *
 * Displays top pricing recommendations as color-coded alert banners.
 * Urgency-colored: critical=red, high=orange, medium=yellow, low=muted.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { PricingRecommendation } from '@/services/metricsAPI';

interface RecommendationsAlertProps {
  recommendations: PricingRecommendation[];
  /** Max number of alerts to show (default 5) */
  maxAlerts?: number;
}

const URGENCY_STYLES: Record<PricingRecommendation['urgency'], string> = {
  critical: 'bg-red-500/15 border-red-500/40 text-red-300',
  high: 'bg-orange-500/15 border-orange-500/40 text-orange-300',
  medium: 'bg-yellow-500/15 border-yellow-500/40 text-yellow-300',
  low: 'bg-white/5 border-white/10 text-white/50',
};

const URGENCY_DOT: Record<PricingRecommendation['urgency'], string> = {
  critical: 'bg-red-400',
  high: 'bg-orange-400',
  medium: 'bg-yellow-400',
  low: 'bg-white/30',
};

const formatTokens = (n: number): string => n.toLocaleString();

export function RecommendationsAlert({
  recommendations,
  maxAlerts = 5,
}: RecommendationsAlertProps): JSX.Element | null {
  const { t } = useTranslation();

  const urgencyOrder: Record<PricingRecommendation['urgency'], number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  };

  const sorted = [...recommendations]
    .sort((a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency])
    .slice(0, maxAlerts);

  if (sorted.length === 0) return null;

  return (
    <div className="space-y-2">
      {sorted.map((rec, idx) => (
        <div
          key={`${rec.operation}-${rec.provider ?? 'all'}-${idx}`}
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${URGENCY_STYLES[rec.urgency]}`}
        >
          <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${URGENCY_DOT[rec.urgency]}`} />
          <div className="flex-1 min-w-0">
            <span className="font-semibold">
              {rec.operation}
              {rec.provider ? ` (${rec.provider})` : ''}
            </span>
            {' — '}
            <span>
              {t('admin.metrics.current')}: {formatTokens(Math.round(rec.current_avg_tokens))} tok{' '}
              &rarr; {t('admin.metrics.recommended')}: {formatTokens(rec.recommended_tokens)} tok (
              {rec.variance_percent > 0 ? '+' : ''}
              {rec.variance_percent.toFixed(1)}%)
            </span>
          </div>
          <span className="flex-shrink-0 text-xs opacity-60 uppercase tracking-wide">
            {t(`admin.metrics.${rec.urgency}`)}
          </span>
        </div>
      ))}
    </div>
  );
}
